import { Brand, DeckPrint, DeckViewer, Frame, Img } from "../headliner/DeckShell";
import { CinematicCover, CINE_ASSETS } from "../headliner/CinematicCover";
import { LogoStamp, Wordmark } from "../headliner/brands";
import { Headliner } from "../buildpartner/BuildCover";
import { WhatItIs } from "../headliner/WhatItIs";
import { Deliver } from "../headliner/HeadlinerDeck";
import { Proposal } from "../headliner/Proposal";
import "../buildpartner/build.css";
import "./law.css";

/* AI Law & Policy Partnership — tredje deck i familien (samme ark, skall og PDF-pipeline). Papir, rolig autoritet.
   Master + mottakerlag: ?for=CMS Kluge|Schjødt|Thommessen|… styrer lockup-logo, «Why»-slide og siste slide.
   Ingen påstander om programstatus: alt scenerelatert er «developed with the partner, subject to programme fit». */

const FIRM_FIRST = {
  "cms kluge": "Part of CMS — one of the world's largest organisations of law firms — with deep Norwegian roots.",
  "schjødt": "One of Norway's largest law firms, with a Nordic footprint and a boardroom-level client base.",
  schjodt: "One of Norway's largest law firms, with a Nordic footprint and a boardroom-level client base.",
  thommessen: "A leading Norwegian business law firm across corporate, technology and regulatory work.",
  wiersholm: "A leading Norwegian business law firm, trusted in the country's most demanding matters.",
  bahr: "The Norwegian firm behind many of the country's largest transactions and board decisions.",
  "wikborg rein": "Norway's most international law firm, with offices in London, Singapore and Shanghai.",
  selmer: "A leading Norwegian business law firm with a strong technology and public-sector practice.",
  haavind: "A leading Norwegian business law firm with a strong technology and public-sector practice.",
};

/* Firmaspesifikke grunner — kun verifiserte fakta (CMS Kluge: cms.law, sep. 2026: grunnlagt 1923, i CMS fra 2021, Oslo/Bergen/Stavanger,
   TMC + personvern, sterkest i energi, infrastruktur og offentlig sektor). Andre firmaer får den generiske argumentasjonen. */
const FIRM_WHY = {
  "cms kluge": [
    ["The European rulebook, locally", "The AI Act is European law. CMS Kluge combines a Norwegian firm founded in 1923 with the CMS network across Europe — the rules where they are written, and where they are applied."],
    ["The sectors in the room", "Energy, infrastructure, technology and the public sector — the sectors that will make Norway's largest AI decisions are the sectors where CMS Kluge is strongest."],
    ["Technology and data as practice", "A TMC and data-protection practice that already advises on technology procurement, cloud, cybersecurity and AI — the questions the room will bring to the Governance Desk."],
    ["Bergen, Oslo, Stavanger", "Offices in the summit's home city and in the country's two other decision centres — the firm is already where the audience is."],
  ],
};

const whyFor = (m) => {
  const own = FIRM_WHY[(m || "").toLowerCase()];
  if (own) return own;
  const first = FIRM_FIRST[(m || "").toLowerCase()];
  const pts = [
    ["The trust category", `AI is now a question of accountability, liability and trust. ${m} at the centre of that conversation is category leadership, not sponsorship.`],
    ["Your clients are in the room", "700 seats for CEOs, board members, GCs and owners — the people who decide what their organisations may build, buy and deploy."],
    ["The rules, anchored", `One firm anchors the rules conversation at the summit — on the main stage, in the closed room and in what the 700 take home. ${m} is the natural firm to do it.`],
  ];
  return first ? [[`Why ${m}`, first], ...pts] : pts;
};

/* 01 Cover — felles kinematisk cover (CinematicCover): Grieghallen full-bleed, Kevin (proposed) i høyre tredjedel, co-brand-lockup */
const Cover = ({ mottaker }) => (
  <CinematicCover
    lockup={<p className="bd-lockup" data-testid="lw-lockup"><Brand size={19} /><i>×</i><Wordmark name={mottaker || "AI Law & Policy Partner"} h={24} /></p>}
    label="AI Law & Policy Partnership"
    title={["Who decides.", "Who is liable.", "Who leads."]}
    sub={<>700 of Norway's leaders set the direction on AI in one room. Every decision in it has a legal dimension — one firm anchors that conversation{mottaker ? <>: <b>{mottaker}</b>.</> : "."}</>}
    index={[
      ["Date", "13 April 2027"],
      ["Venue", "Grieghallen, Bergen"],
      [mottaker ? "Prepared for" : "Audience", mottaker || "700 seats · invitation-only", mottaker ? "prep" : "", "hd-prepared"],
    ]}
  />
);

/* 02 The question — tesen + deckets objekt: en styreagenda der AI har blitt et juridisk punkt, og firmaet sitter ved bordet */
const AGENDA = [
  ["01", "Approval of minutes", "", true],
  ["02", "CFO report · Q1", "", true],
  ["03", "AI — accountability when systems act", "Decision"],
  ["04", "EU AI Act · obligations from August 2026", "Briefing"],
  ["05", "Data, IP and the models we build on", "Decision"],
  ["06", "Liability, insurance and disclosure", "Briefing"],
];

const Question = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The question" mottaker={mottaker} className="lw-q">
    <h2 className="bd-q-h" data-testid="lw-q-h">Every material<br />AI decision has<br />a legal dimension.</h2>
    <p className="lw-q-p" data-testid="lw-q-p">Who is accountable when AI acts? What may be deployed, on what data, under which rules? Boards are asking now — and on 13 April, 700 of them ask at once.</p>
    <div className="lw-agenda" data-testid="lw-agenda" aria-hidden>
      <div className="hd"><span className="k">Board of Directors</span><span className="d">Agenda · June 2027 · Extract</span></div>
      {AGENDA.map(([n, t, s, muted], i) => (
        <div className={`row ${muted ? "muted" : ""}`} key={n} style={{ "--i": i }}>
          <span className="n">{n}</span>
          <span className="t">{t}</span>
          {s && <span className={`s ${s === "Decision" ? "on" : ""}`}>{s}</span>}
        </div>
      ))}
      <div className="ft"><span className="k">External counsel</span><span className="w"><Wordmark name={mottaker || "AI Law & Policy Partner"} h={16} /></span></div>
    </div>
    <p className="bd-q-cap lw-q-cap" data-testid="lw-q-cap"><i className="dot" />The boardroom after AI — and the firm at the table</p>
    <p className="bd-foot">This deck is about putting one law firm at the centre of that conversation — on stage, in the room and in what the 700 take home.</p>
  </Frame>
);

/* 04 The room — designet sammensetning (mål), invite-only */
const ROOM = [
  ["CEOs and board members", 40],
  ["General counsel, CFOs and executives", 25],
  ["Founders and technology leaders", 20],
  ["Owners and investors", 10],
  ["Public-sector leaders", 5],
];

export const Room = ({ nr, mottaker, sub = <>700 seats. Curated for the people deciding<br />how AI is built, bought and governed.</> }) => (
  <Frame nr={nr} label="The room" mottaker={mottaker} className="hd-room lw-room">
    <div className="hd-big" data-testid="lw-room-700">700</div>
    <p className="hd-sub lw-room-sub">{sub}</p>
    <div className="lw-comp" data-testid="lw-room-comp">
      <span className="k">Target audience composition</span>
      {ROOM.map(([l, p]) => (
        <div className="row" key={l}>
          <span className="l">{l}</span>
          <span className="bar"><i style={{ width: `${p}%` }} /></span>
          <span className="p">{p}%</span>
        </div>
      ))}
      <span className="note">Designed composition — the invitation model, not a promise of named attendees.</span>
    </div>
  </Frame>
);

/* 05 Why law — det juridiske landskapet, uten å overdrive */
const LAW = [
  ["Regulation", "The EU AI Act applies in stages from 2025 to 2027. Norway is implementing it through the EEA. Boards need to know what it means for them — now."],
  ["Accountability", "When AI systems act, who is responsible? Governance, documentation and oversight are becoming board questions."],
  ["Data & IP", "What can organisations train on, build on and own? The value of AI runs through data rights and IP."],
  ["Risk & disclosure", "Liability, insurance, procurement and disclosure — AI is entering the risk register of every listed company."],
];

const WhyLaw = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why law" mottaker={mottaker} className="lw-law">
    <h2 className="hd-h2 bd-h80 lw-law-h" data-testid="lw-law-h">One of the defining governance<br />questions of a generation.</h2>
    <ul className="hd-ledger lw-ledger" data-testid="lw-law-points">
      {LAW.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="bd-foot">The technology moves fast. The rules are being written. The people writing strategy need the people who read the rules.</p>
  </Frame>
);

/* 06 Why <firm> */
const WhyYou = ({ nr, mottaker }) => (
  <Frame nr={nr} label={`Why ${mottaker}`} mottaker={mottaker} className="hd-whyyou">
    <h2 className="hd-whyyou-h" data-testid="lw-whyyou-h">Why {mottaker}.</h2>
    <ul className="hd-whyyou-ledger" data-testid="lw-whyyou-points">
      {whyFor(mottaker).map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <LogoStamp name={mottaker} sub="AI Law & Policy Partner · Norwegian AI Summit 2027" />
    <p className="hd-whyyou-foot" data-testid="lw-whyyou-foot">That makes {mottaker} the natural AI Law &amp; Policy Partner.</p>
  </Frame>
);

/* 07 The position — tre eierskap, konkrete tall */
const POSITION = [
  ["The position", ["Official AI Law & Policy Partner", "Category exclusivity — the only law firm in the partner programme", "Law-firm speaking time on the main stage reserved for the partner", "Presence across the summit's relevant surfaces"]],
  ["The conversation", ["The Rules Segment — 12 minutes on the main stage, a confirmed element of the partnership", "The AI Board Briefing — a closed session for 15–25 board members, partner hosted", "The AI Board Brief — co-created, distributed to all 700"]],
  ["The access", ["10 seats for the firm and its clients", "A table of 10 at the Summit Dinner", "Curated introductions through the day", "Recording rights to your own segment"]],
];

const Position = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="lw-pos">
    <h2 className="hd-h2 bd-h80" data-testid="lw-pos-h">One firm anchors<br />the rules conversation.</h2>
    <div className="lw-cols" data-testid="lw-pos-cols">
      {POSITION.map(([t, items]) => (
        <div className="col" key={t}>
          <span className="t">{t}</span>
          <ul>{items.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      ))}
    </div>
    <p className="hd-one-univ" data-testid="lw-pos-universe">
      {UNIVERS.map((u, i) => (
        <span key={u} className={u === "AI Law & Policy" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
      ))}
    </p>
  </Frame>
);

/* 08 The Rules Segment — garantert scenetid (12 min, main stage); klokkeslett = foreslått plassering, satt med programmet */
const RULES = [
  ["12:00", "Proposed slot", "The last main-stage word before lunch — the whole room in the same seat. The final slot is set with the programme."],
  ["12 min", "A briefing, not a commercial", "What the AI Act and the new duties mean for Norwegian boards — the brief is set together with the summit."],
  ["1 voice", "Delivered by the partner's lead", "The firm's partner on the main stage, introduced as the AI Law & Policy Partner."],
  ["Yours", "Recorded and cleared", "The partner may use the recording of its own segment in its own channels."],
];

const Rules = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Rules Segment" mottaker={mottaker} className="lw-rules">
    <h2 className="hd-h2 bd-h80" data-testid="lw-rules-h">12 minutes. Main stage.<br />The rules, explained by {mottaker || "the partner"}.</h2>
    <div className="lw-rules-grid" data-testid="lw-rules-points">
      {RULES.map(([t, l, d]) => (
        <div className="hd-block" key={l}>
          <span className="t">{t}</span>
          <span className="l">{l}</span>
          <span className="d">{d}</span>
        </div>
      ))}
    </div>
    <p className="bd-foot">A confirmed element of the partnership. The editorial framing is what makes it credible — thought leadership, not sponsorship.</p>
  </Frame>
);

/* 09 The Board Briefing — den mest eksklusive flaten */
const BRIEF = [
  ["Invitation only", "15–25 at Chair, CEO, board, GC and CFO level — 8 seats for the partner's own clients, the rest curated by the summit"],
  ["Closed room", "No recording, no sales pitch. Chatham House Rule — what is said may be used, who said it may not"],
  ["Partner hosted", "The firm hosts and facilitates; the summit curates the room. Working theme: the board's AI duties in 2027 — set together"],
  ["Designed into the day", "30–45 minutes, placed in the programme — not squeezed between sessions. Proposed 15:00, set with the programme"],
];

const Briefing = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The AI Board Briefing" mottaker={mottaker} className="hd-exec lw-brief">
    <h2 className="hd-h2 big" data-testid="lw-brief-h">15–25 board members.<br />One closed room.</h2>
    <p className="hd-exec-sub">A private briefing on what AI now demands of boards — hosted by {mottaker || "the AI Law & Policy Partner"}, with a curated group of board members, executives and owners from the summit's audience.</p>
    <div className="hd-exec-grid" data-testid="lw-brief-points">
      {BRIEF.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="hd-exec-foot">The most senior room of the day — and the firm's name on the door.</p>
  </Frame>
);

/* 09 Beyond the stage — flater med funksjon */
const BEYOND = [
  ["The AI Governance Desk", "A purposeful advisory point in the AI Experience — concrete governance questions, short conversations, meetings booked for later. Presence with a function, not square metres.", "/images/vision-expo-v3.webp"],
  ["The network", "CEOs · board members · GCs · founders · investors — curated introductions through the day.", "/images/vision-networking-v3.webp"],
  ["Summit Dinner", "The conversation continues after the stage closes.", "/images/vision-dinner-v4.webp"],
];

const Beyond = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Beyond the stage" mottaker={mottaker} className="hd-beyond">
    <h2 className="hd-h2" data-testid="lw-beyond-h">Presence with a function.</h2>
    <div className="hd-frags" data-testid="lw-beyond-frags">
      {BEYOND.map(([n, d, img]) => (
        <figure className="hd-frag" key={n}>
          <div className="ph"><Img src={img} alt={n} draggable={false} /></div>
          <figcaption><span className="n">{n}</span><span className="d">{d}</span></figcaption>
        </figure>
      ))}
    </div>
  </Frame>
);

/* 11 What the 700 take home — innhold, rettigheter og rapport */
const TAKE = [
  ["The AI Board Brief", "A co-created post-summit briefing on the governance questions raised on stage — distributed to all 700 participants, with the firm as author."],
  ["Content & broadcast", "Integration in the summit's content and broadcast where relevant — and recording rights to the firm's own segment."],
  ["The partner report", "Within 30 days: attendance, composition, AI Board Briefing participants, content reach — the numbers the firm needs internally."],
  ["The follow-up", "A partner-hosted executive breakfast or client session after the summit, built on the AI Board Briefing."],
];

const Content = ({ nr, mottaker }) => (
  <Frame nr={nr} label="After the day" mottaker={mottaker} className="lw-take">
    <h2 className="hd-h2 bd-h80" data-testid="lw-take-h">What the 700 take home<br />carries your name.</h2>
    <ul className="hd-ledger lw-ledger" data-testid="lw-take-points">
      {TAKE.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="bd-foot">Developed together. Nothing here is a brochure promise — it is a programme we build with the partner.</p>
  </Frame>
);

/* 12 The day */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)"], ["11:30", "Leadership programme"], ["12:00", "The Rules Segment", true],
  ["13:00", "AI Experience · Governance Desk", true], ["15:00", "The AI Board Briefing", true], ["16:00", "Closing keynote"], ["18:30", "Dinner · Spissen"],
];

const Day = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day">
    <h2 className="hd-h2" data-testid="lw-day-h">Three moments in a designed day.</h2>
    <ol className="hd-timeline" data-testid="lw-timeline">
      {DAG.map(([t, l, hi]) => (
        <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span></li>
      ))}
    </ol>
    <p className="hd-dayfoot">Highlighted: the AI Law &amp; Policy Partner's moments. The Rules Segment and the AI Board Briefing are confirmed elements of the partnership; times shown are proposed and set with the programme. The Governance Desk is designed with the partner.</p>
  </Frame>
);

/* 13 The economics — struktur uten tall; nivå deles i samtale */
const INCLUDED = [
  "The AI Law & Policy Partner position, category exclusive", "The Rules Segment — 12 minutes, main stage", "The AI Board Briefing — hosted, 15–25 seats",
  "The AI Board Brief to all 700", "10 seats · a table of 10 at the Summit Dinner", "The AI Governance Desk in the AI Experience",
  "Content, broadcast and recording rights", "The partner report",
];
const OPTIONAL = ["Additional seats and dinner tables", "A hosted client dinner the evening before", "Production of the post-summit client session"];

const Economics = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The economics" mottaker={mottaker} className="lw-econ">
    <h2 className="hd-h2 bd-h80" data-testid="lw-econ-h">One fee.<br />Everything in the position.</h2>
    <div className="lw-econ-grid" data-testid="lw-econ">
      <div className="col">
        <span className="t">Included</span>
        <ul>{INCLUDED.map((x) => <li key={x}>{x}</li>)}</ul>
      </div>
      <div className="col">
        <span className="t">Optional</span>
        <ul>{OPTIONAL.map((x) => <li key={x}>{x}</li>)}</ul>
        <p className="lvl" data-testid="lw-econ-level">Indicative level shared in conversation. One partner — decided by the summit, not auctioned.</p>
      </div>
    </div>
  </Frame>
);

/* 15 One firm */
const SCARCE = ["One AI Law & Policy Partner", "No other law firm in the partner programme", "Law-firm speaking time on the main stage reserved for the partner", "Decided by the summit — not sold twice"];
const UNIVERS = ["Founding", "Headliner", "Frontier", "Build", "AI Law & Policy", "Dinner", "Broadcast"];

export const OneFirm = ({ nr, mottaker }) => (
  <Frame nr={nr} label="One firm" mottaker={mottaker} className="hd-one">
    <h2 className="hd-one-h" data-testid="lw-one-h">One category.<br />One AI Law &amp; Policy Partner.</h2>
    <p className="hd-one-sub">One firm owns the conversation about the rules of AI at Norwegian AI Summit — with category exclusivity.</p>
    <p className="hd-one-line" data-testid="lw-one-line">One law firm · one category · no competing position.</p>
    <ul className="hd-one-pts" data-testid="lw-one-points">{SCARCE.map((k) => <li key={k}>{k}</li>)}</ul>
    <p className="hd-one-univ" data-testid="lw-one-universe">
      {UNIVERS.map((u, i) => (
        <span key={u} className={u === "AI Law & Policy" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
      ))}
    </p>
  </Frame>
);

/* 16 On one page — det partneren sender videre internt */
const ONEPAGE = (m) => [
  ["What", `${m || "One law firm"} as the AI Law & Policy Partner of Norwegian AI Summit 2027 — category exclusive.`],
  ["Why", "One room, built for 700 of Norway's leaders, sets the direction on AI. Every material decision has a legal dimension. One firm anchors that conversation."],
  ["You get", "The Rules Segment (12 min, main stage) · The AI Board Briefing (15–25, closed room) · The AI Board Brief to all 700 · The AI Governance Desk · 10 seats and a dinner table · recording rights · partner report."],
  ["Investment", "One fee, category exclusive. Indicative level shared in conversation."],
  ["Timeline", "Position confirmed by 1 October 2026 · designed together Q4 2026 · announced with the programme."],
];

const OnePage = ({ nr, mottaker }) => (
  <Frame nr={nr} label="On one page" mottaker={mottaker} className="lw-onepage">
    <h2 className="hd-h2 bd-h80" data-testid="lw-onepage-h">On one page.</h2>
    <dl className="lw-onepage-grid" data-testid="lw-onepage">
      {ONEPAGE(mottaker).map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  </Frame>
);

/* 17 Next step — prosess og frist */
const STEPS = [
  ["01", "Conversation", "A 45-minute meeting on the position and the firm's ambitions."],
  ["02", "Term sheet", "One page: position, deliverables, fee, exclusivity — and data, content and recording rights."],
  ["03", "Design together", "The Rules Segment, the AI Board Briefing and the Brief — Q4 2026."],
  ["04", "Announcement", "Announced with the programme, as the AI Law & Policy Partner."],
];

const Next = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Next step" mottaker={mottaker} className="hd-next lw-next">
    <h2 className="hd-next-h" data-testid="lw-next-h">One AI Law &amp; Policy Partnership.</h2>
    <p className="hd-next-p" data-testid="lw-next-p">
      13 April 2027. Grieghallen, Bergen. Built for 700 of Norway's decision-makers.<br />
      {mottaker ? `We would like to explore ${mottaker} as the AI Law & Policy Partner.` : "We are selecting one law firm for the AI Law & Policy position."}
    </p>
    <ol className="lw-steps" data-testid="lw-steps">
      {STEPS.map(([n, t, d]) => (
        <li key={n}><span className="n">{n}</span><span className="t">{t}</span><span className="d">{d}</span></li>
      ))}
    </ol>
    <p className="hd-next-terms lw-deadline" data-testid="lw-deadline"><span className="k">Decision</span>Position confirmed by <b>1 October 2026</b> · designed together Q4 2026 · announced with the programme.</p>
    <div className="hd-contact" data-testid="hd-contact">
      <span className="n">Martin · Founder, Norwegian AI Summit</span>
      <span className="e">martin@norwegianaisummit.no</span>
    </div>
  </Frame>
);

const LawHeadliner = (p) => <Headliner {...p} lead="Proposed opening keynote. The international headliner sets the ambition level of the room — and the questions that follow him on stage are questions of accountability, regulation and trust." />;
const LawDeliver = (p) => <Deliver {...p} label="Built to deliver" />;
const LawProposal = ({ nr, mottaker }) => (
  <Proposal
    nr={nr} mottaker={mottaker}
    sentence={<><b>{mottaker}</b> as the AI Law &amp; Policy Partner of Norwegian AI Summit 2027 — the firm at the centre of the rules conversation.</>}
    index={[["The Rules Segment", "12 minutes · main stage"], ["The AI Board Briefing", `15–25 · closed room · hosted by ${mottaker}`], ["Position", "One law firm. Category exclusive."]]}
  />
);

/* «One firm» (knapphet) kuttet etter gjennomgang — eksklusiviteten sies på Position, Economics og One page; universlinjen ligger på Position */
const SLIDES = [Cover, WhatItIs, Question, Room, LawHeadliner, WhyLaw, Position, Rules, Briefing, Beyond, Content, Day, Economics, LawDeliver, OnePage, Next];
/* Personalisert: «Why <firma>» etter Why law, «The proposal» som signatur til slutt */
const slidesFor = (mottaker) => (mottaker ? [...SLIDES.slice(0, 6), WhyYou, ...SLIDES.slice(6), LawProposal] : SLIDES);

const ASSETS = ["/images/headliner/kevin-src-hi.webp", ...CINE_ASSETS, "/images/vision-hall-keynote.webp", "/images/vision-expo-v3.webp", "/images/vision-networking-v3.webp", "/images/vision-dinner-v4.webp"];

export function LawPrint() {
  return <DeckPrint slides={slidesFor} fallback="" theme="paper" venue="none" />;
}

export default function LawDeck({ mottaker = "", publicMode = false }) {
  return <DeckViewer deck="law" slides={slidesFor} fallback={mottaker} filePrefix="Norwegian-AI-Summit-AI-Law-Policy-Partnership" assets={ASSETS} theme="paper" venue="none" categoryField={false} publicMode={publicMode} />;
}
